import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-blacklist');

/**
 * The differentiated claim here is that the lists are NOT equal, and which ones you can ignore.
 * Every competing article presents "90+ blacklists" as if a hit on any of them is equivalent,
 * which sends people into a panic over UCEPROTECT L3 while missing an SBL listing.
 */
export default function EmailBlacklistPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'An email blacklist, properly a DNSBL, is a DNS-published list of IP addresses or domains suspected of sending spam. Receivers query it during the SMTP conversation and decide what to do. There are hundreds; perhaps six meaningfully affect whether your mail is delivered.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="How a lookup actually works">
        <p>
          A DNSBL is not a database you query over an API. It is an ordinary DNS zone, and a listing
          is an ordinary A record. To ask whether <Tok>203.0.113.7</Tok> is listed, a receiver
          reverses the octets and looks up a name:
        </p>
        <LearnCode>
          {`7.113.0.203.zen.spamhaus.org      A?
→ 127.0.0.4     listed — and the last octet says WHICH sub-list
→ NXDOMAIN      not listed`}
        </LearnCode>
        <p>
          Two consequences follow. Lookups are effectively free and instantaneous, which is why
          receivers do them on every connection. And the return value carries meaning: the final
          octet distinguishes sub-lists with very different implications, which is why "you are on
          Spamhaus" is not a diagnosis on its own.
        </p>
      </LearnSection>

      <LearnSection title="The lists are not equal">
        <p>
          This is the part that gets flattened everywhere else. Checking against ninety lists is
          easy; knowing which results matter is the whole skill.
        </p>
        <LearnTable
          head={['List', 'Who uses it', 'If you are on it']}
          rows={[
            ['Spamhaus ZEN (SBL/CSS/XBL/PBL)', <Lead key="a">Almost everyone</Lead>, <Lead key="b">Act today. Highest-impact listing there is</Lead>],
            ['Spamhaus DBL (domains)', 'Widely', 'Act today — this one follows you across IPs'],
            ['Barracuda', 'Barracuda appliances, common in enterprise', 'Worth resolving; free removal form'],
            ['SpamCop', 'Moderate; some hosts use it advisory-only', 'Auto-expires in about 24 hours; fix the cause'],
            ['SORBS', 'Declining', 'Low priority'],
            ['UCEPROTECT L1', 'Niche', 'Low priority'],
            ['UCEPROTECT L2 / L3', <Lead key="c">Almost nobody</Lead>, <Lead key="d">Ignore. See below</Lead>],
            ['Most of the remaining long tail', 'Effectively nobody', 'Informational only'],
          ]}
        />
        <p>
          A hit on Spamhaus ZEN and a hit on an obscure regional list are not comparable events, and
          a tool that reports them identically is giving you a number rather than an answer.
        </p>
      </LearnSection>

      <LearnSection title="UCEPROTECT levels 2 and 3, specifically">
        <p>
          Worth its own section because it generates more unnecessary alarm than any other listing.
        </p>
        <LearnList>
          <li><Lead>Level 1</Lead> lists individual IPs that actually sent spam. Legitimate, if aggressive.</li>
          <li><Lead>Level 2</Lead> lists the entire allocation your IP sits in, because <em>someone else</em> in it sent spam.</li>
          <li><Lead>Level 3</Lead> lists the whole autonomous system — every IP your hosting provider operates.</li>
        </LearnList>
        <p>
          On L2 or L3 you are listed for being a customer of a large hosting provider. You did
          nothing, you can do nothing, and hardly any receiver consults those levels. Express
          delisting is available for a fee, which is a fact worth weighing when deciding how much
          authority to grant the listing.
        </p>
        <p>
          Check whether it is L1 before spending any time on it. L1 is a real signal about your own
          sending; L2 and L3 are a signal about your neighbours.
        </p>
      </LearnSection>

      <LearnSection title="IP listings and domain listings are different problems">
        <LearnTable
          head={['', 'IP-based (SBL, XBL, Barracuda…)', 'Domain-based (DBL, SURBL, URIBL)']}
          rows={[
            ['Lists', 'The sending server’s address', 'A domain appearing in the message'],
            ['On a shared ESP IP', <Lead key="a">Often not your doing</Lead>, <Lead key="b">Always yours</Lead>],
            ['Escaping it', 'Change IP, or get the IP delisted', 'No escape — the domain is the identity'],
            ['Typical cause', 'Compromised host, bad neighbour, sudden volume', 'Your domain appeared in spam sent by someone else, or your own list is bad'],
          ]}
        />
        <p>
          Domain listings are the more serious of the two and the more often overlooked. An IP
          listing can be outrun by moving; a domain listing follows the domain everywhere, including
          to a new ESP. Domains also get listed for appearing as a <em>link</em> in spam — so an
          affiliate or a compromised page on your site can list you without you sending anything.
        </p>
      </LearnSection>

      <LearnSection title="What to do about a listing that matters">
        <LearnSubsection title="Find the cause before requesting removal">
          <p>
            Delisting without fixing the cause gets you relisted, usually within days, and repeated
            removal requests are themselves a negative signal. Most listings trace to one of four
            things: a compromised account or server sending without your knowledge, a sudden volume
            increase that looked like a burst, a list containing spam traps, or a genuinely bad
            neighbour on a shared IP.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Then use the list’s own process">
          <p>
            Every reputable list publishes one, and it is always free. Spamhaus has a removal form
            per sub-list. Barracuda has a form. SpamCop expires automatically once the reports stop.
            Nothing legitimate charges you.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Treat paid express delisting as what it is">
          <p>
            Some lists offer it. That is a commercial arrangement, not a technical remedy, and it
            says something about how much weight receivers should give the list. Never pay to be
            removed from a list you have not confirmed anyone consults.
          </p>
        </LearnSubsection>
        <LearnSubsection title="Then monitor, because listings arrive silently">
          <p>
            Nothing notifies you. Delivery simply degrades, and you find out from a customer.{' '}
            <Link to="/tools/blacklist-checker" className="text-accent underline underline-offset-4">
              Check on demand
            </Link>
            , or have{' '}
            <Link to="/features/blacklist-checks" className="text-accent underline underline-offset-4">
              something check daily
            </Link>{' '}
            for you.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="A listing is one input, not a verdict">
        <p>
          Receivers combine it with authentication, engagement and their own history with you. A
          domain with strong{' '}
          <Link to="/learn/sender-reputation" className="text-accent underline underline-offset-4">
            sender reputation
          </Link>{' '}
          can carry a minor listing with little visible effect; a new domain with none cannot.
        </p>
        <p>
          Which cuts both ways. Being listed nowhere does not mean your mail is delivering — plenty
          of{' '}
          <Link to="/learn/why-email-goes-to-spam" className="text-accent underline underline-offset-4">
            spam placement
          </Link>{' '}
          happens with no listing involved at all.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'How many blacklists should I actually care about?',
    a: 'Roughly six. Spamhaus ZEN and DBL carry the most weight by a wide margin, then Barracuda and SpamCop. Most of the long tail is consulted by almost nobody and a hit there is informational rather than actionable.',
  },
  {
    q: 'I am listed on UCEPROTECT level 2. What do I do?',
    a: 'Almost certainly nothing. Level 2 lists an entire IP allocation because someone else in it sent spam, so you are listed for being a customer of a large host. Very few receivers consult it. Confirm you are not also on level 1, which is about your own sending, and move on.',
  },
  {
    q: 'How do I get delisted?',
    a: 'Find and fix the cause first, then use the list’s own removal form — every reputable list publishes one and it is free. Requesting removal without fixing the cause gets you relisted within days, and repeated requests are themselves a negative signal.',
  },
  {
    q: 'What is the difference between an IP listing and a domain listing?',
    a: 'An IP listing targets the sending server and can be escaped by moving to a different IP. A domain listing targets your domain and follows it everywhere, including to a new provider. Domain listings are the more serious of the two and the more often missed.',
  },
  {
    q: 'I am listed but my mail still gets delivered. How?',
    a: 'Because a listing is one input among many. Receivers weigh it against your authentication, your engagement history and their own record of you. A minor listing on a list few consult may have no visible effect at all.',
  },
  {
    q: 'Should I pay for express delisting?',
    a: 'No. Every list that receivers genuinely rely on removes you for free once the cause is fixed. A list charging for removal is telling you something about its own business model, and paying it does not improve your delivery anywhere that matters.',
  },
];
